import { AxiosResponse } from "axios";

import { DocumentDataField } from "../../../constants";
import { AltiusRes, IDeal, IDealsTableRow, IFolder, IInvestor } from "../../../store/types";
import { ITaggerDocument } from "../../../store/types/tagger/api";
import { AsyncQueries, AsyncQueriesParams } from "../base";

export interface ITaggerQueries {
  getDeals: () => Promise<IDealsTableRow[]>;
  getDealById: (id: IDeal["id"]) => Promise<IDeal>;
  getDocumentData: (docId: string) => Promise<ITaggerDocument>;
  changeDocumentData: (docId: string, field: DocumentDataField, value: any) => Promise<any>;
  getDealFolders: (dealId: number) => Promise<IFolder[]>;
  getFolderData: (folderId: number) => Promise<Pick<IFolder, "id" | "title">>;
  getDealInvestors: (dealId: number) => Promise<IInvestor[]>;
  setAsync: () => Promise<void>;
}

export class TaggerQueries extends AsyncQueries implements ITaggerQueries {
  public constructor(params: AsyncQueriesParams) {
    super(params);
  }

  public getDeals = async () => {
    const res: AxiosResponse<AltiusRes<IDealsTableRow[]>> = await this.api.post({
      url: "/api/v0.0.2/deals-list",
      data: { filters: {} }
    });
    res.data.data.sort((d1, d2) => d1.title.localeCompare(d2.title));
    return res.data.data;
  };

  public getDealById = (id: number): Promise<IDeal> =>
    this.api.get({ url: `/api/v0.0.2/deals/${id}` }).then((res) => res.data);

  public getDocumentData = async (docId: string): Promise<ITaggerDocument> => {
    const res = await this.api.get<AltiusRes<ITaggerDocument>>({ url: `/api/v0.0.3/pdf/doc/${docId}` });
    return res.data.data;
  };

  public getDealFolders = async (dealId: number): Promise<IFolder[]> => {
    return await this.api
      .get({ url: `/api/v0.0.3/deals/${dealId}/folders` })
      .then((res) => Object.values(res.data.data));
  };

  public getFolderData = async (folderId: number): Promise<Pick<IFolder, "id" | "title">> => {
    return await this.api.get({ url: `/api/v0.0.3/folders/${folderId}` }).then((res) => res.data.data);
  };

  public changeDocumentData = (docId: string, field: DocumentDataField, value: any) => {
    let apiField = "";
    switch (field) {
      case DocumentDataField.SelectedCurrency:
        apiField = "currency";
        break;
      case DocumentDataField.SelectedDate:
        apiField = "date";
        break;
      case DocumentDataField.SelectedInvestor:
        apiField = "investor_id";
        break;
      case DocumentDataField.SelectedRound:
        apiField = "round_id";
        break;
      default:
        apiField = "";
        break;
    }

    return this.api.patch({
      url: `/api/v0.0.3/pdf/doc/${docId}`,
      data: {
        [apiField]: value
      }
    });
  };

  public getDealInvestors = async (dealId: number) => {
    return await this.api
      .post({
        url: "/api/v0.0.3/investors/search",
        data: {
          deal_id: dealId
        }
      })
      .then((res) => res.data.data);
  };

  public setAsync = () => {
    return this.api.post({ url: "/textract/analyze-documents/set-async" }).then(() => {});
  };
}
